import React,{useState,useEffect} from "react";
import {C,CC,fmt,today,gCalc,filterByDate} from "../utils/constants";
import BarcodeScannerModal from "./BarcodeScanner";

// ── UI Components ─────────────────────────────────────────────────
function Sect({title,icon,children}){
  return(
    <div style={{marginBottom:16}}>
      <div style={{fontSize:12,fontWeight:700,color:C.sub,marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
        <span>{icon}</span><span style={{textTransform:"uppercase",letterSpacing:1}}>{title}</span>
      </div>
      <div style={{background:"#fff",borderRadius:14,padding:16,border:`1px solid ${C.border}`,boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
        {children}
      </div>
    </div>
  );
}

function FRow({label,children}){
  return(
    <div style={{marginBottom:12}}>
      <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle={
  width:"100%",padding:"11px 14px",borderRadius:10,
  border:`1.5px solid ${C.border}`,fontSize:13,
  background:"#F8FAFC",outline:"none",
  fontFamily:"inherit",boxSizing:"border-box",
  transition:"border 0.2s"
};

// ── NEW ORDER PAGE ─────────────────────────────────────────────────
export function NewOrderPage({companies,custs,prods,orders,onSubmit,onCancel,printSettings={},canAccess}){
  const showItemDisc = printSettings.showDiscount !== false;
  const showOverallDisc = printSettings.showOverallDisc !== false;
  const showMRP = printSettings.showMRP !== false;
  const showFreeQty = printSettings.showFreeQty !== false;
  const showHSN = printSettings.showHSN !== false;
  const [cid,setCid]=useState("");
  const [cust,setCust]=useState("");
  const [date,setDate]=useState(today());
  const [gt,setGt]=useState("CGST_SGST");
  const [ps,setPs]=useState("");
  const [items,setItems]=useState([]);
  const [odp,setOdp]=useState(0);
  const [paidNow,setPaidNow]=useState(0);
  const [notes,setNotes]=useState("");
  const [billType,setBillType]=useState("tax_invoice"); // tax_invoice | cash_bill
  const [err,setErr]=useState("");
  const [smartSug,setSmartSug]=useState(null);
  const [showScanner,setShowScanner]=useState(false);

  const co=companies.find(c=>c.id===cid);
  const cobj=custs.find(c=>c.name===cust);
  const filtProds=prods.filter(p=>!ps||p.name.toLowerCase().includes(ps.toLowerCase()));

  const handleBarcodeScan=(code)=>{
    setShowScanner(false);
    const match=prods.find(p=>p.barcode&&p.barcode===code);
    if(match){ addIt(match); setErr(""); }
    else setErr(`⚠️ No product found for barcode "${code}"`);
  };

  // Smart suggestions
  useEffect(()=>{
    if(!cust||!orders) return;
    const custOrders=(orders||[]).filter(o=>o.customer_name===cust||o.cust===cust).slice(-5);
    if(!custOrders.length){setSmartSug(null);return;}
    const freq={};
    custOrders.flatMap(o=>o.items||[]).forEach(i=>{if(i.name)freq[i.name]=(freq[i.name]||0)+(i.qty||1);});
    const top=Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,5);
    setSmartSug(top.length?top.map(([name,qty])=>({name,avgQty:Math.round(qty/custOrders.length)||1})):null);
  },[cust]);

  // Calculations
  const itot=items.reduce((s,i)=>s+(i.price-i.disc)*i.qty,0);
  const odisc=Math.round(itot*odp/100);
  // GST is computed PER ITEM using each product's own rate (5/12/18/28%) — a single
  // flat company-level rate would overcharge or undercharge whenever an invoice mixes
  // products from different GST slabs. Each item also respects its own "price already
  // includes GST" setting: for inclusive items the taxable value is back-calculated out
  // of the entered price rather than adding GST on top of it.
  const itemCalcs=items.map(i=>{
    const itemNet=(i.price-i.disc)*i.qty;
    const itemNetAfterOdisc=itemNet-(itemNet*odp/100);
    const rate=i.gst||18;
    const itemTaxable=i.gst_inclusive ? itemNetAfterOdisc/(1+rate/100) : itemNetAfterOdisc;
    return {itemTaxable, itemGst:itemTaxable*rate/100};
  });
  const tax=itemCalcs.reduce((s,c)=>s+c.itemTaxable,0);
  const gstAmt=itemCalcs.reduce((s,c)=>s+c.itemGst,0);
  const g=gt==="CGST_SGST" ? {c:gstAmt/2,s:gstAmt/2,i:0,t:gstAmt} : {c:0,s:0,i:gstAmt,t:gstAmt};
  const grand=Math.round(tax+g.t);
  // For the summary display only — show the common rate if every item shares one,
  // otherwise label it "Mixed" (the correct total is always computed per-item above)
  const uniqueRates=[...new Set(items.map(i=>i.gst||18))];
  const rateLabel=uniqueRates.length===1?uniqueRates[0]:"Mixed";

  const addIt=p=>{
    const ex=items.find(i=>i.id===p.id);
    ex?setItems(items.map(i=>i.id===p.id?{...i,qty:i.qty+1}:i))
      :setItems([...items,{id:p.id,name:p.name,hsn:p.hsn||"",mrp:p.mrp||0,price:p.sp||0,qty:1,free:0,disc:0,dp:0,gst:p.gst||18,gst_inclusive:!!p.gst_inclusive,amount:(p.sp||0)}]);
  };
  const removeIt=id=>setItems(items.filter(i=>i.id!==id));
  const updQty=(id,qty)=>{
    if(qty<1){removeIt(id);return;}
    setItems(items.map(i=>i.id===id?{...i,qty:+qty,amount:(i.price-i.disc)*(+qty)}:i));
  };
  const updFree=(id,free)=>setItems(items.map(i=>i.id===id?{...i,free:Math.max(0,+free||0)}:i));
  const updDisc=(id,dp)=>{
    setItems(items.map(i=>{if(i.id!==id)return i;const disc=Math.round(i.price*(+dp)/100);return{...i,dp:+dp,disc,amount:(i.price-disc)*i.qty};}));
  };

  const go=()=>{
    if(!cid){setErr("Please select a company");return;}
    if(!cust&&billType!=="cash_bill"){setErr("Please select a customer");return;}
    if(!items.length){setErr("Please add at least one product");return;}
    if(cobj&&(cobj.credit_limit||0)>0&&((cobj.bal||0)+grand)>cobj.credit_limit){
      const ok=window.confirm(`⚠️ Credit Limit Alert!\n\n${cust}'s credit limit is ₹${fmt(cobj.credit_limit)}.\nAfter this order, total due will be ₹${fmt((cobj.bal||0)+grand)}.\n\nProceed anyway?`);
      if(!ok)return;
    }
    setErr("");
    onSubmit({cid,cust,cobj,gt,items,odp:+odp,odisc,tax,gamt:Math.round(g.t),total:grand,paid:+paidNow,notes,date,bill_type:billType});
  };

  return(
    <div style={{fontFamily:"system-ui,sans-serif",background:C.bg,minHeight:"100vh"}}>
      {/* Header */}
      <div style={{background:C.side,padding:"0 14px",height:52,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:100,boxShadow:"0 2px 8px rgba(0,0,0,0.15)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={onCancel} style={{background:"rgba(255,255,255,0.1)",border:"none",color:"#fff",width:34,height:34,borderRadius:9,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>←</button>
          <div>
            <div style={{color:"#fff",fontWeight:800,fontSize:15}}>📦 New Order</div>
            {items.length>0&&<div style={{color:"#94A3B8",fontSize:10}}>{items.length} items • ₹{fmt(grand)}</div>}
          </div>
        </div>
        <button onClick={go} style={{background:C.success,border:"none",color:"#fff",padding:"8px 18px",borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit",boxShadow:"0 2px 8px rgba(16,185,129,0.4)"}}>
          ✅ Save
        </button>
      </div>

      <div style={{padding:"14px 14px 100px",maxWidth:600,margin:"0 auto"}}>

        {/* Error */}
        {err&&<div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:10,padding:"10px 14px",color:C.danger,fontSize:12,fontWeight:600,marginBottom:12}}>⚠️ {err}</div>}

        {/* SECTION 1 — DETAILS */}
        <Sect title="Order Details" icon="📋">
          {/* Company */}
          <FRow label="🏢 Company *">
            <select value={cid} onChange={e=>setCid(e.target.value)} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1.5px solid ${cid?C.primary:C.border}`,fontSize:13,fontFamily:"inherit",outline:"none",background:"#fff",color:cid?C.text:C.sub}}>
              <option value="">-- Company Select Karo --</option>
              {companies.filter(c=>c.active).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FRow>

          {/* Bill Type */}
          <FRow label="🧾 Bill Type">
            <select value={billType} onChange={e=>{
              setBillType(e.target.value);
              if(e.target.value==="cash_bill"&&!cust) setCust("Cash Customer");
            }} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1.5px solid ${C.primary}`,fontSize:13,fontFamily:"inherit",outline:"none",background:"#fff",cursor:"pointer",fontWeight:600}}>
              <option value="tax_invoice">🧾 Tax Invoice — GST registered customer</option>
              <option value="cash_bill">💵 Cash Bill — Walk-in / retail customer</option>
            </select>
            {billType==="cash_bill"&&<div style={{fontSize:10,color:C.warning,marginTop:4,padding:"3px 6px",background:"#FFF7ED",borderRadius:5}}>
              💡 Cash Bill mein GST details optional hote hain
            </div>}
            {billType==="tax_invoice"&&<div style={{fontSize:10,color:C.info,marginTop:4,padding:"3px 6px",background:"#EFF6FF",borderRadius:5}}>
              💡 ₹50,000+ bills mein customer naam zaroori (GST rule)
            </div>}
          </FRow>

          {/* Customer */}
          <FRow label={billType==="cash_bill"?"👥 Customer (Optional)":"👥 Customer *"}>
            <select value={cust} onChange={e=>setCust(e.target.value)} style={{...inputStyle,border:`1.5px solid ${cust?C.success:C.border}`}}>
              <option value="">-- Please select a customer --</option>
              {[...custs].sort((a,b)=>a.name.localeCompare(b.name)).map(c=><option key={c.id} value={c.name}>{c.name} {(c.bal||0)>0?`(Due: ₹${fmt(c.bal)})`:""}</option>)}
            </select>
            {cobj&&<div style={{marginTop:8,padding:"8px 12px",background:"#F0FDF4",borderRadius:8,fontSize:11}}>
              <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:4}}>
                <span>📞 {cobj.phone||"—"}</span>
                <span>📍 {cobj.city||"—"}</span>
                {cobj.gstin&&<span>GST: {cobj.gstin}</span>}
              </div>
              {(cobj.bal||0)>0&&<div style={{color:C.danger,fontWeight:700,marginTop:4}}>⚠️ Existing Due: ₹{fmt(cobj.bal)}</div>}
              {(cobj.credit_limit||0)>0&&<div style={{marginTop:4,fontWeight:700,color:((cobj.bal||0)+grand)>cobj.credit_limit?C.danger:C.sub}}>
                💳 Credit Limit: ₹{fmt(cobj.credit_limit)} {((cobj.bal||0)+grand)>cobj.credit_limit&&<span>— ⚠️ This order will EXCEED the limit! (New total due: ₹{fmt((cobj.bal||0)+grand)})</span>}
              </div>}
            </div>}
          </FRow>

          {/* Date + GST */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <FRow label="📅 Date">
              <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={inputStyle}/>
            </FRow>
            <FRow label="💰 GST Type">
              <select value={gt} onChange={e=>setGt(e.target.value)} style={inputStyle}>
                <option value="CGST_SGST">CGST + SGST</option>
                <option value="IGST">IGST</option>
                <option value="NONE">No GST</option>
              </select>
            </FRow>
          </div>
        </Sect>

        {/* SECTION 2 — PRODUCTS */}
        <Sect title="Products" icon="🧴">
          {/* Search */}
          <div style={{display:"flex",gap:7,marginBottom:10}}>
            <div style={{position:"relative",flex:1}}>
              <input value={ps} onChange={e=>setPs(e.target.value)} placeholder="🔍 Search products..." style={{...inputStyle,paddingLeft:14}}/>
              {ps&&<button onClick={()=>setPs("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:16,color:C.sub}}>✕</button>}
            </div>
            {(!canAccess||canAccess("barcode"))&&<button onClick={()=>setShowScanner(true)} style={{padding:"0 16px",borderRadius:10,border:`1.5px solid ${C.info}`,background:"#EFF6FF",color:C.info,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>📷 Scan</button>}
          </div>

          {/* Smart Suggestions */}
          {smartSug&&!ps&&<div style={{marginBottom:10,padding:"10px 12px",background:"#F0FFF4",borderRadius:10,border:`1px solid ${C.success}30`}}>
            <div style={{fontSize:11,fontWeight:700,color:C.success,marginBottom:6}}>🤖 Pichle order se suggestions:</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {smartSug.map((s,i)=>{
                const prod=prods.find(p=>p.name===s.name);
                return <button key={i} onClick={()=>{if(prod)addIt(prod);}} style={{padding:"4px 10px",borderRadius:20,background:C.success+"15",border:`1px solid ${C.success}40`,fontSize:11,fontWeight:600,cursor:"pointer",color:C.success,fontFamily:"inherit"}}>
                  + {s.name} (avg {s.avgQty})
                </button>;
              })}
            </div>
          </div>}

          {/* Product List — sirf tab dikhao jab search ho */}
          <div style={{maxHeight:320,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
            {!ps&&items.length===0&&<div style={{textAlign:"center",padding:"16px",color:"#94A3B8",fontSize:12}}>
              🔍 Product name type karo ya neeche se select karo
            </div>}
            {(ps?filtProds:filtProds.filter(p=>items.find(i=>i.id===p.id))).slice(0,ps?20:50).map(p=>{
              const pco=companies.find(c=>c.id===p.cid);
              const inCart=items.find(i=>i.id===p.id);
              return(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:inCart?"#EFF6FF":"#F8FAFC",borderRadius:10,border:`1.5px solid ${inCart?pco?.color||C.primary:C.border}`,transition:"all 0.2s"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                      <span style={{fontWeight:700,fontSize:12}}>{p.name}</span>
                      {pco&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:5,background:pco.color+"20",color:pco.color,fontWeight:700}}>{pco.short}</span>}
                      {p.unit&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:5,background:C.info+"15",color:C.info}}>{p.unit}</span>}
                    </div>
                    <div style={{fontSize:10,color:C.sub,marginTop:2}}>
                      {p.mrp&&<span style={{textDecoration:"line-through",marginRight:6}}>₹{p.mrp}</span>}
                      <span style={{fontWeight:700,color:C.primary}}>₹{p.sp||0}</span>
                      <span style={{marginLeft:6}}>Stock: {p.stock}</span>
                    </div>
                  </div>
                  {inCart?(
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <button onClick={()=>updQty(p.id,inCart.qty-1)} style={{width:28,height:28,borderRadius:7,border:`1.5px solid ${pco?.color||C.primary}`,background:"#fff",color:pco?.color||C.primary,cursor:"pointer",fontWeight:800,fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                      <span style={{fontWeight:800,color:pco?.color||C.primary,minWidth:24,textAlign:"center",fontSize:14}}>{inCart.qty}</span>
                      <button onClick={()=>addIt(p)} style={{width:28,height:28,borderRadius:7,background:pco?.color||C.primary,border:"none",color:"#fff",cursor:"pointer",fontWeight:800,fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                    </div>
                  ):(
                    <button onClick={()=>addIt(p)} style={{width:32,height:32,borderRadius:8,background:pco?.color||C.primary,border:"none",color:"#fff",cursor:"pointer",fontWeight:800,fontSize:18,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                  )}
                </div>
              );
            })}
            {ps&&filtProds.length===0&&<div style={{textAlign:"center",color:C.sub,padding:20,fontSize:12}}>Koi product nahi mila — "{ps}" ke liye</div>}
            {ps&&filtProds.length>20&&<div style={{textAlign:"center",color:C.sub,fontSize:11,padding:8}}>Aur products ke liye search refine karein ({filtProds.length} total)</div>}
          </div>
        </Sect>

        {/* SECTION 3 — CART */}
        {items.length>0&&<Sect title={`Cart — ${items.length} Items`} icon="🛒">
          {items.map(it=>(
            <div key={it.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13}}>{it.name}</div>
                  <div style={{fontSize:10,color:C.sub}}>₹{it.price} per unit</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,background:C.bg,borderRadius:8,padding:"4px 8px"}}>
                    <button onClick={()=>updQty(it.id,it.qty-1)} style={{width:24,height:24,borderRadius:6,border:`1px solid ${C.border}`,background:"#fff",cursor:"pointer",fontWeight:800,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                    <input type="number" value={it.qty} onChange={e=>updQty(it.id,+e.target.value)} style={{width:36,textAlign:"center",border:"none",background:"transparent",fontWeight:800,fontSize:14,fontFamily:"inherit",outline:"none"}}/>
                    <button onClick={()=>updQty(it.id,it.qty+1)} style={{width:24,height:24,borderRadius:6,border:`1px solid ${C.border}`,background:"#fff",cursor:"pointer",fontWeight:800,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                  </div>
                  <div style={{fontWeight:800,color:C.primary,minWidth:60,textAlign:"right"}}>₹{fmt((it.price-it.disc)*it.qty)}</div>
                  <button onClick={()=>removeIt(it.id)} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:C.danger,padding:2}}>🗑️</button>
                </div>
              </div>
              {/* Price + Discount row */}
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:C.sub}}>Rate ₹:</span>
                <input type="number" value={it.price} onChange={e=>setItems(items.map(i=>i.id===it.id?{...i,price:+e.target.value,disc:Math.round(+e.target.value*(i.dp||0)/100)}:i))} min={0} style={{width:70,padding:"3px 6px",borderRadius:6,border:`1px solid ${C.border}`,fontSize:11,textAlign:"center",fontFamily:"inherit"}}/>
                {showItemDisc&&<><span style={{fontSize:11,color:C.sub}}>Disc %:</span>
                <input type="number" value={it.dp||0} onChange={e=>updDisc(it.id,e.target.value)} min={0} max={100} style={{width:50,padding:"3px 6px",borderRadius:6,border:`1px solid ${C.border}`,fontSize:11,textAlign:"center",fontFamily:"inherit"}}/></>}
                {it.disc>0&&<span style={{fontSize:10,color:C.success}}>-₹{fmt(it.disc*it.qty)}</span>}
                {showFreeQty&&<><span style={{fontSize:11,color:C.success,fontWeight:600,marginLeft:"auto"}}>🎁 Free:</span>
                <input type="number" value={it.free||0} onChange={e=>updFree(it.id,e.target.value)} min={0} placeholder="0" style={{width:50,padding:"3px 6px",borderRadius:6,border:`1px solid ${C.success}`,fontSize:11,textAlign:"center",fontFamily:"inherit",background:"#F0FDF4"}}/></>}
              </div>
              {showFreeQty&&(+it.free||0)>0&&<div style={{fontSize:10,color:C.success,fontWeight:600,marginTop:3}}>📦 Customer gets {it.qty+(+it.free)} units total ({it.qty} billed + {it.free} free)</div>}
              {it.gst_inclusive&&<div style={{fontSize:10,color:"#92400E",fontWeight:600,marginTop:3,background:"#FFFBEB",display:"inline-block",padding:"2px 7px",borderRadius:6}}>💰 GST included in ₹{it.price} (not added on top)</div>}
            </div>
          ))}
        </Sect>}

        {/* SECTION 4 — TOTALS */}
        {items.length>0&&<Sect title="Bill Summary" icon="💰">
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
              <span style={{color:C.sub}}>Subtotal</span>
              <span style={{fontWeight:600}}>₹{fmt(itot)}</span>
            </div>

            {showOverallDisc&&<>{/* Overall Discount */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13}}>
              <span style={{color:C.sub}}>Overall Discount %</span>
              <input type="number" value={odp} onChange={e=>setOdp(+e.target.value)} min={0} max={100} style={{width:70,padding:"5px 8px",borderRadius:7,border:`1.5px solid ${C.border}`,fontSize:12,textAlign:"center",fontFamily:"inherit"}}/>
            </div></>}
            {showOverallDisc&&odisc>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
              <span style={{color:C.success}}>Discount Applied</span>
              <span style={{color:C.success,fontWeight:600}}>-₹{fmt(odisc)}</span>
            </div>}

            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
              <span style={{color:C.sub}}>Taxable Amount</span>
              <span style={{fontWeight:600}}>₹{fmt(tax)}</span>
            </div>

            {gt==="CGST_SGST"?<>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                <span style={{color:C.sub}}>CGST ({rateLabel==="Mixed"?"Mixed":rateLabel/2}%)</span>
                <span>₹{fmt(g.c)}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                <span style={{color:C.sub}}>SGST ({rateLabel==="Mixed"?"Mixed":rateLabel/2}%)</span>
                <span>₹{fmt(g.s)}</span>
              </div>
            </>:<div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
              <span style={{color:C.sub}}>IGST ({rateLabel}%)</span>
              <span>₹{fmt(g.i)}</span>
            </div>}

            <div style={{borderTop:`2px solid ${C.border}`,paddingTop:10,display:"flex",justifyContent:"space-between",fontSize:16,fontWeight:800}}>
              <span>Grand Total</span>
              <span style={{color:C.primary}}>₹{fmt(grand)}</span>
            </div>

            {/* Paid Now */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13,background:C.bg,padding:"8px 12px",borderRadius:8}}>
              <span style={{fontWeight:600}}>💳 Paid Now (optional)</span>
              <input type="number" value={paidNow} onChange={e=>setPaidNow(+e.target.value)} min={0} max={grand} style={{width:90,padding:"6px 8px",borderRadius:7,border:`1.5px solid ${C.border}`,fontSize:13,textAlign:"right",fontFamily:"inherit"}}/>
            </div>
            {paidNow>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
              <span style={{color:C.danger}}>Remaining Due</span>
              <span style={{color:C.danger,fontWeight:700}}>₹{fmt(grand-paidNow)}</span>
            </div>}

            {/* Notes */}
            <div>
              <div style={{fontSize:11,color:C.sub,marginBottom:4}}>📝 Notes (optional)</div>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Add a note..." rows={2} style={{width:"100%",padding:"8px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,resize:"none",fontFamily:"inherit",boxSizing:"border-box",outline:"none"}}/>
            </div>
          </div>
        </Sect>}

        {/* Save Button */}
        <button onClick={go} style={{width:"100%",padding:"15px",borderRadius:13,border:"none",background:items.length?C.success:"#ccc",color:"#fff",fontSize:15,fontWeight:800,cursor:items.length?"pointer":"not-allowed",fontFamily:"inherit",boxShadow:items.length?"0 4px 16px rgba(16,185,129,0.4)":"none",transition:"all 0.2s"}}>
          {items.length?`✅ Save Order — ₹${fmt(grand)}`:"Add products before saving"}
        </button>
      </div>
      {showScanner&&<BarcodeScannerModal onClose={()=>setShowScanner(false)} onScan={handleBarcodeScan}/>}
    </div>
  );
}

// ── NEW PURCHASE PAGE ──────────────────────────────────────────────
export function NewPurPage({companies,supps,prods,onSubmit,onCancel,printSettings={},canAccess}){
  const showFreeQty = printSettings.showFreeQty !== false;
  const [cid,setCid]=useState("");
  const [supp,setSupp]=useState("");
  const [date,setDate]=useState(today());
  const [ps,setPs]=useState("");
  const [items,setItems]=useState([]);
  const [paidNow,setPaidNow]=useState(0);
  const [notes,setNotes]=useState("");
  const [err,setErr]=useState("");
  const [suppInvNo,setSuppInvNo]=useState("");
  const [showScanner,setShowScanner]=useState(false);

  const filtProds=prods.filter(p=>{
    const nameMatch=!ps||p.name.toLowerCase().includes(ps.toLowerCase())||p.brand?.toLowerCase().includes(ps.toLowerCase());
    const compMatch=!cid||p.cid===cid||!p.cid; // Show company products first
    return nameMatch&&compMatch;
  }).sort((a,b)=>{
    // Sort: selected company first, then others
    const aMatch=a.cid===cid?0:1;
    const bMatch=b.cid===cid?0:1;
    return aMatch-bMatch || a.name.localeCompare(b.name);
  });

  const addIt=p=>{
    const ex=items.find(i=>i.pid===p.id);
    ex?setItems(items.map(i=>i.pid===p.id?{...i,qty:i.qty+1}:i))
      :setItems([...items,{pid:p.id,name:p.name,hsn:p.hsn||"",qty:1,free:0,cost:p.pp||0,gst:p.gst||18,gst_inclusive:!!p.gst_inclusive}]);
  };
  const removeIt=pid=>setItems(items.filter(i=>i.pid!==pid));
  const NUMERIC_FIELDS=['qty','free','cost','gst'];
  const updIt=(pid,field,val)=>setItems(items.map(i=>i.pid===pid?{...i,[field]:NUMERIC_FIELDS.includes(field)?+val:val}:i));

  const handleBarcodeScan=(code)=>{
    setShowScanner(false);
    const match=prods.find(p=>p.barcode&&p.barcode===code);
    if(match){ addIt(match); setErr(""); }
    else setErr(`⚠️ No product found for barcode "${code}"`);
  };

  // Respects each item's "cost already includes GST" setting — for inclusive items the
  // taxable value is back-calculated out of the entered cost rather than adding GST on top.
  const purCalcs=items.map(i=>{
    const itemAmt=i.cost*i.qty;
    const rate=i.gst||18;
    const itemTaxable=i.gst_inclusive ? itemAmt/(1+rate/100) : itemAmt;
    return {itemTaxable, itemGst:itemTaxable*rate/100};
  });
  const sub=purCalcs.reduce((s,c)=>s+c.itemTaxable,0);
  const gstAmt=purCalcs.reduce((s,c)=>s+c.itemGst,0);
  const total=Math.round(sub+gstAmt);

  const go=()=>{
    if(!cid){setErr("Please select a company");return;}
    if(!supp){setErr("Please select a supplier");return;}
    if(!items.length){setErr("Please add at least one product");return;}
    setErr("");
    onSubmit({cid,supp,date,items,sub,gstAmt:Math.round(gstAmt),total,paid:+paidNow,notes,suppInvNo:suppInvNo?.trim()||""});
  };

  return(
    <div style={{fontFamily:"system-ui,sans-serif",background:C.bg,minHeight:"100vh"}}>
      {/* Header */}
      <div style={{background:C.side,padding:"0 14px",height:52,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:100,boxShadow:"0 2px 8px rgba(0,0,0,0.15)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={onCancel} style={{background:"rgba(255,255,255,0.1)",border:"none",color:"#fff",width:34,height:34,borderRadius:9,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center"}}>←</button>
          <div>
            <div style={{color:"#fff",fontWeight:800,fontSize:15}}>🛒 New Purchase</div>
            {items.length>0&&<div style={{color:"#94A3B8",fontSize:10}}>{items.length} items • ₹{fmt(total)}</div>}
          </div>
        </div>
        <button onClick={go} style={{background:C.success,border:"none",color:"#fff",padding:"8px 18px",borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>✅ Save</button>
      </div>

      <div style={{padding:"14px 14px 100px",maxWidth:600,margin:"0 auto"}}>
        {err&&<div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:10,padding:"10px 14px",color:C.danger,fontSize:12,fontWeight:600,marginBottom:12}}>⚠️ {err}</div>}

        {/* DETAILS */}
        <Sect title="Purchase Details" icon="📋">
          <FRow label="🏢 Company *">
            <select value={cid} onChange={e=>setCid(e.target.value)} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1.5px solid ${cid?C.primary:C.border}`,fontSize:13,fontFamily:"inherit",outline:"none",background:"#fff",color:cid?C.text:C.sub}}>
              <option value="">-- Company Select Karo --</option>
              {companies.filter(c=>c.active).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FRow>

          <FRow label="🏭 Supplier *">
            <select value={supp} onChange={e=>setSupp(e.target.value)} style={{...inputStyle,border:`1.5px solid ${supp?C.success:C.border}`}}>
              <option value="">-- Please select a supplier --</option>
              {supps.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </FRow>

          <FRow label="📅 Date">
            <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={inputStyle}/>
          </FRow>
          <FRow label="🧾 Supplier Invoice No. (Optional)">
            <input value={suppInvNo} onChange={e=>setSuppInvNo(e.target.value)} placeholder="e.g. SI-2025-001" style={inputStyle}/>
          </FRow>
        </Sect>

        {/* PRODUCTS */}
        <Sect title="Products" icon="🧴">
          <div style={{display:"flex",gap:7,marginBottom:10}}>
            <div style={{position:"relative",flex:1}}>
              <input value={ps} onChange={e=>setPs(e.target.value)} placeholder="🔍 Product search..." style={inputStyle}/>
              {ps&&<button onClick={()=>setPs("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:16,color:C.sub}}>✕</button>}
            </div>
            {(!canAccess||canAccess("barcode"))&&<button onClick={()=>setShowScanner(true)} style={{padding:"0 16px",borderRadius:10,border:`1.5px solid ${C.info}`,background:"#EFF6FF",color:C.info,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>📷 Scan</button>}
          </div>
          <div style={{maxHeight:280,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
            {!ps&&<div style={{textAlign:"center",padding:"14px",color:"#94A3B8",fontSize:12}}>🔍 Product name type karo search mein</div>}
            {ps&&filtProds.slice(0,20).map(p=>{
              const pco=companies.find(c=>c.id===p.cid);
              const inCart=items.find(i=>i.pid===p.id);
              return(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:inCart?"#F0FFF4":"#F8FAFC",borderRadius:10,border:`1.5px solid ${inCart?C.success:C.border}`}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>
                      <span style={{fontWeight:700,fontSize:12}}>{p.name}</span>
                      {pco&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:5,background:pco.color+"20",color:pco.color,fontWeight:700}}>{pco.short}</span>}
                    </div>
                    <div style={{fontSize:10,color:C.sub}}>Purchase: ₹{p.pp||0} • Stock: {p.stock}</div>
                  </div>
                  {inCart?(
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <button onClick={()=>updIt(p.id,'qty',inCart.qty-1)} style={{width:26,height:26,borderRadius:6,border:`1px solid ${C.success}`,background:"#fff",color:C.success,cursor:"pointer",fontWeight:800,fontSize:14}}>−</button>
                      <span style={{fontWeight:800,minWidth:20,textAlign:"center"}}>{inCart.qty}</span>
                      <button onClick={()=>addIt(p)} style={{width:26,height:26,borderRadius:6,background:C.success,border:"none",color:"#fff",cursor:"pointer",fontWeight:800,fontSize:14}}>+</button>
                    </div>
                  ):(
                    <button onClick={()=>addIt(p)} style={{width:30,height:30,borderRadius:8,background:C.success,border:"none",color:"#fff",cursor:"pointer",fontWeight:800,fontSize:16}}>+</button>
                  )}
                </div>
              );
            })}
          </div>
        </Sect>

        {/* CART */}
        {items.length>0&&<Sect title={`Items — ${items.length}`} icon="🛒">
          {items.map(it=>(
            <div key={it.pid} style={{padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontWeight:700,fontSize:13}}>{it.name}</div>
                <button onClick={()=>removeIt(it.pid)} style={{background:"none",border:"none",cursor:"pointer",color:C.danger,fontSize:14}}>🗑️</button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:showFreeQty?"1fr 1fr 1fr 1fr":"1fr 1fr 1fr",gap:6}}>
                <div>
                  <div style={{fontSize:10,color:C.sub,marginBottom:3}}>Billed Qty</div>
                  <input type="number" value={it.qty} onChange={e=>updIt(it.pid,'qty',e.target.value)} min={1} style={{width:"100%",padding:"6px 8px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12,fontFamily:"inherit",textAlign:"center"}}/>
                </div>
                {showFreeQty&&<div>
                  <div style={{fontSize:10,color:C.success,marginBottom:3,fontWeight:700}}>🎁 Free Qty</div>
                  <input type="number" value={it.free||0} onChange={e=>updIt(it.pid,'free',e.target.value)} min={0} placeholder="0" style={{width:"100%",padding:"6px 8px",borderRadius:7,border:`1px solid ${C.success}`,fontSize:12,fontFamily:"inherit",textAlign:"center",background:"#F0FDF4"}}/>
                </div>}
                <div>
                  <div style={{fontSize:10,color:C.sub,marginBottom:3}}>Cost ₹</div>
                  <input type="number" value={it.cost} onChange={e=>updIt(it.pid,'cost',e.target.value)} min={0} style={{width:"100%",padding:"6px 8px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12,fontFamily:"inherit"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,color:C.sub,marginBottom:3}}>GST %</div>
                  <select value={it.gst} onChange={e=>updIt(it.pid,'gst',e.target.value)} style={{width:"100%",padding:"6px 8px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12,fontFamily:"inherit"}}>
                    {[0,5,12,18,28].map(r=><option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:5}}>
                {(+it.free||0)>0&&<div style={{fontSize:10,color:C.success,fontWeight:700}}>📦 Total received: {(+it.qty||0)+(+it.free||0)} units ({it.qty} paid + {it.free} free)</div>}
                <div style={{textAlign:"right",fontSize:12,fontWeight:700,color:C.primary,marginLeft:"auto"}}>
                  ₹{fmt(it.cost*it.qty)} + GST ₹{fmt(it.cost*it.qty*it.gst/100)} = ₹{fmt(it.cost*it.qty*(1+it.gst/100))}
                </div>
              </div>
              {(!canAccess||canAccess("batch"))&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginTop:6}}>
                <div>
                  <div style={{fontSize:10,color:C.sub,marginBottom:3}}>🏷️ Batch No (optional)</div>
                  <input value={it.batch_no||""} onChange={e=>updIt(it.pid,'batch_no',e.target.value)} placeholder="e.g. B2405" style={{width:"100%",padding:"6px 8px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,color:C.sub,marginBottom:3}}>📅 Expiry Date (optional)</div>
                  <input type="date" value={it.expiry_date||""} onChange={e=>updIt(it.pid,'expiry_date',e.target.value)} style={{width:"100%",padding:"6px 8px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>
                </div>
              </div>}
            </div>
          ))}
        </Sect>}

        {/* TOTALS */}
        {items.length>0&&<Sect title="Bill Summary" icon="💰">
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
              <span style={{color:C.sub}}>Subtotal</span>
              <span style={{fontWeight:600}}>₹{fmt(sub)}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
              <span style={{color:C.sub}}>GST</span>
              <span style={{fontWeight:600}}>₹{fmt(gstAmt)}</span>
            </div>
            <div style={{borderTop:`2px solid ${C.border}`,paddingTop:10,display:"flex",justifyContent:"space-between",fontSize:16,fontWeight:800}}>
              <span>Grand Total</span>
              <span style={{color:C.primary}}>₹{fmt(total)}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13,background:C.bg,padding:"8px 12px",borderRadius:8}}>
              <span style={{fontWeight:600}}>💳 Paid Now</span>
              <input type="number" value={paidNow} onChange={e=>setPaidNow(+e.target.value)} min={0} max={total} style={{width:90,padding:"6px 8px",borderRadius:7,border:`1.5px solid ${C.border}`,fontSize:13,textAlign:"right",fontFamily:"inherit"}}/>
            </div>
            {paidNow>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
              <span style={{color:C.danger}}>Remaining Due</span>
              <span style={{color:C.danger,fontWeight:700}}>₹{fmt(total-paidNow)}</span>
            </div>}
            <div>
              <div style={{fontSize:11,color:C.sub,marginBottom:4}}>📝 Notes</div>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Add a note..." rows={2} style={{width:"100%",padding:"8px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,resize:"none",fontFamily:"inherit",boxSizing:"border-box",outline:"none"}}/>
            </div>
          </div>
        </Sect>}

        <button onClick={go} style={{width:"100%",padding:"15px",borderRadius:13,border:"none",background:items.length?C.primary:"#ccc",color:"#fff",fontSize:15,fontWeight:800,cursor:items.length?"pointer":"not-allowed",fontFamily:"inherit",boxShadow:items.length?"0 4px 16px rgba(37,99,235,0.4)":"none"}}>
          {items.length?`✅ Save Purchase — ₹${fmt(total)}`:"Add products before saving"}
        </button>
      </div>
      {showScanner&&<BarcodeScannerModal onClose={()=>setShowScanner(false)} onScan={handleBarcodeScan}/>}
    </div>
  );
}
